import { FormattedMessage } from "react-intl";
import useSearchOutletContext from "../layout/outletContext.ts";
import ResultsTemplate from "../common/ResultsTemplate.tsx";
import AssetSwimlane from "../../common/asset-swimlane/AssetSwimlane.tsx";
import TitleResultCard from "./TitleResultCard.tsx";
import styles from "./SearchTitleResults.module.scss";
import ResponsiveAssetListAuto from "../../../../common/features/asset/ResponsiveAssetListAuto.tsx";
import useReaderSearchReturn from "../common/useReaderSearchReturn.ts";
import { useLayoutEffect, useRef } from "react";
import MoreButton from "../../../../common/core-ui/MoreButton.tsx";
import SearchTitleResult from "./SearchTitleResult.tsx";
import SearchTitleCounts from "./SearchTitleCounts.tsx";
import { getFocusableElements } from "../../../../common/utils/dom.ts";

export default function SearchTitleResults() {
  const { query, titleResults, waitForTabFlip } = useSearchOutletContext();
  const {
    filter,
    setFilter,
    items,
    totalItems,
    hasMore,
    loadMore,
    exploreTotal,
    storeTotal,
    isLoadingMore,
  } = titleResults.allResults;
  const { status } = titleResults;
  const containerRef = useRef<HTMLUListElement>(null);
  useReaderSearchReturn(containerRef);
  const lastCardRef = useRef<HTMLLIElement | null>();

  useLayoutEffect(() => {
    if (status === "has-results" && containerRef.current) {
      if (isLoadingMore) {
        // Once we start loading more, note the last card
        lastCardRef.current =
          containerRef.current.querySelector<HTMLLIElement>("li:last-of-type");
      } else {
        // And then once it has loaded focus the next one
        const nextCard = lastCardRef.current?.nextElementSibling;
        if (nextCard) {
          nextCard.scrollIntoView(false);
          getFocusableElements(nextCard as HTMLElement)[0]?.focus();
        }
        lastCardRef.current = null;
      }
    }
  }, [isLoadingMore, status]);

  if (waitForTabFlip) {
    // if redirecting to content tab, don't draw as causes a flash
    return null;
  }
  const renderAllTitleResults = () => {
    if (items.length) {
      return (
        <>
          <FormattedMessage defaultMessage="All Title Results" tagName="h2" />
          <SearchTitleCounts
            value={filter}
            query={query}
            curCount={items.length}
            totalCount={totalItems}
            exploreTotal={exploreTotal}
            storeTotal={storeTotal}
            onChange={(value) => setFilter(value)}
            onClear={() => setFilter("allResults")}
          />
          <ul ref={containerRef} className={styles.allTitles}>
            {items.map((asset, index) => (
              <SearchTitleResult
                asset={asset}
                lazyLoad={index > 20}
                key={asset.manageAsset.vbid}
              />
            ))}
          </ul>
          <MoreButton
            loading={isLoadingMore}
            hasMore={hasMore}
            wrapped
            onMoreClick={loadMore}
          />
        </>
      );
    }
  };

  const renderLibraryResultsCards = (className?: string) => {
    return titleResults.licenseResults.items.map((asset, index) => (
      <TitleResultCard
        asset={asset}
        lazyLoad={index > 20}
        key={asset.manageAsset.vbid}
        className={className}
      />
    ));
  };

  return (
    <ResultsTemplate
      isLoading={status === "loading"}
      isEmpty={status === "empty"}
    >
      {titleResults.licenseResults.items.length > 0 && (
        <div className={styles.library}>
          <FormattedMessage
            defaultMessage="Title Results in My Library"
            tagName="h2"
          />
          {titleResults.allResults.items.length ? (
            <AssetSwimlane>{renderLibraryResultsCards()}</AssetSwimlane>
          ) : (
            <ResponsiveAssetListAuto>
              {renderLibraryResultsCards()}
            </ResponsiveAssetListAuto>
          )}
        </div>
      )}
      {renderAllTitleResults()}
    </ResultsTemplate>
  );
}
